import { useEffect, useRef } from 'react';
import { useMotionReduced } from './motion';

/**
 * Shared visual hooks for MoodMate — small, dependency-free, and conservative
 * about motion so the rich effects never fight accessibility or performance.
 */

/**
 * True when MoodMate motion should be reduced. Driven by the in-app motion
 * preference (see visual/motion.ts), NOT the raw OS media query — so turning off
 * OS animations no longer silently freezes the whole feature; the default is
 * full motion, with an explicit in-app toggle to reduce it.
 */
export const useReducedMotion = (): boolean => useMotionReduced();

/**
 * Gentle pointer parallax: sets --mm-px / --mm-py (each roughly -1..1) on a ref
 * element as the pointer moves across it, for layers to consume via translate().
 *
 * Desktop fine-pointer only, and disabled under reduced motion — touch devices and
 * motion-sensitive users get the calm static scene. Throttled to one update per
 * animation frame so it stays cheap.
 */
export function usePointerParallax<T extends HTMLElement>(enabled = true) {
  const ref = useRef<T | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || reduced) return;
    if (!window.matchMedia?.('(pointer: fine)').matches) return;

    let frame = 0;
    const onMove = (e: PointerEvent) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const r = el.getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width - 0.5) * 2;
        const py = ((e.clientY - r.top) / r.height - 0.5) * 2;
        el.style.setProperty('--mm-px', px.toFixed(3));
        el.style.setProperty('--mm-py', py.toFixed(3));
      });
    };
    const onLeave = () => {
      el.style.setProperty('--mm-px', '0');
      el.style.setProperty('--mm-py', '0');
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
    };
  }, [enabled, reduced]);

  return ref;
}
